import React from 'react';
import { PRESETS } from '../constants/competition';
import CompetitionSettingsFields from './CompetitionSettingsFields';
import { Field, Section } from './ui';

function NewCompView({ canCreateCompetition, getEffectiveDefaults, clubs, compForm, createComp, currentUser, customTemplates, deleteTemplate, endDateTouched, isSuperAdmin, openCompetitionRulesPicker, restoreSiteDefaults, saveAsTemplate, saveClubDefaults, selectedClubId, selectedPreset, setCompForm, setSelectedPreset, setShowCustomSettings, showCustomSettings }) {
  return (
    <div className="page-container-md">

      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
        <h2 style={{ marginBottom: '4px' }}><i className="fa fa-plus-circle w3-text-theme w3-margin-right"></i>New Competition</h2>
        <span className="w3-text-grey" style={{ fontSize: '1.05em', fontStyle: 'italic' }}>
          {(() => { const cid = selectedClubId || (currentUser && currentUser.ClubId); const c = clubs.find(cl => String(cl.id) === String(cid)); return c ? c.name : (isSuperAdmin ? 'All Clubs (Site Defaults)' : ''); })()}
        </span>
      </div>
      <p className="w3-text-grey" style={{ marginTop: 0 }}>Choose a preset or build a custom competition</p>

      {!canCreateCompetition && (
        <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round" style={{ marginTop: 0 }}>
          <strong>Select a specific club.</strong> Competitions can only be created for one club, not while the All Clubs filter is active.
        </div>
      )}

      {/* Preset grid — built-in + system templates + club templates */}
      <div className="preset-grid">
        {PRESETS.map(p => (
          <div key={p.key}
            className={`w3-card w3-round preset-card ${selectedPreset === p.key ? 'preset-selected' : 'w3-white'}`}
            onClick={() => {
              setSelectedPreset(p.key);
              endDateTouched.current = false;
              const year = new Date().getFullYear();
              if (p.settings) {
                setCompForm(f => ({ ...getEffectiveDefaults(), ...p.settings, name: `${year} ${p.label}` }));
                setShowCustomSettings(false);
              } else {
                setCompForm(f => ({ ...getEffectiveDefaults(), name: f.name }));
                setShowCustomSettings(true);
              }
            }} style={{ cursor: 'pointer' }}>
            <div className="w3-padding w3-center">
              <i className={`fa ${p.icon} w3-xlarge w3-text-theme`}></i>
              <p style={{ margin: '6px 0 2px', fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{p.label}</p>
              <p className="w3-tiny w3-text-grey" style={{ margin: 0 }}>{p.desc}</p>
            </div>
          </div>
        ))}
        {customTemplates.filter(t => !t.ClubId).map(t => (
          <div key={`system-${t.id}`}
            className={`w3-card w3-round preset-card ${selectedPreset === `custom-${t.id}` ? 'preset-selected' : 'w3-white'}`}
            onClick={() => {
              setSelectedPreset(`custom-${t.id}`);
              endDateTouched.current = false;
              const year = new Date().getFullYear();
              setCompForm(f => ({ ...getEffectiveDefaults(), ...t.settings, name: `${year} ${t.label}` }));
              setShowCustomSettings(false);
            }} style={{ cursor: 'pointer', position: 'relative' }}>
            {isSuperAdmin && (
              <button className="w3-button w3-tiny w3-text-grey w3-hover-red w3-round"
                onClick={e => deleteTemplate(t.id, e)}
                title="Delete preset"
                style={{ position: 'absolute', top: '4px', right: '4px', padding: '2px 6px', zIndex: 1 }}>
                <i className="fa fa-times"></i>
              </button>
            )}
            <div className="w3-padding w3-center">
              <i className={`fa ${t.icon || 'fa-trophy'} w3-xlarge w3-text-theme`}></i>
              <p style={{ margin: '6px 0 2px', fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{t.label}</p>
              <p className="w3-tiny w3-text-grey" style={{ margin: 0 }}>{t.desc}</p>
            </div>
          </div>
        ))}
        {customTemplates.filter(t => t.ClubId).map(t => (
          <div key={`custom-${t.id}`}
            className={`w3-card w3-round preset-card ${selectedPreset === `custom-${t.id}` ? 'preset-selected' : 'w3-pale-blue'}`}
            onClick={() => {
              setSelectedPreset(`custom-${t.id}`);
              endDateTouched.current = false;
              const year = new Date().getFullYear();
              setCompForm(f => ({ ...getEffectiveDefaults(), ...t.settings, name: `${year} ${t.label}` }));
              setShowCustomSettings(false);
            }} style={{ cursor: 'pointer', position: 'relative' }}>
            <button className="w3-button w3-tiny w3-text-grey w3-hover-red w3-round"
              onClick={e => deleteTemplate(t.id, e)}
              title="Delete template"
              style={{ position: 'absolute', top: '4px', right: '4px', padding: '2px 6px', zIndex: 1 }}>
              <i className="fa fa-times"></i>
            </button>
            <div className="w3-padding w3-center">
              <i className={`fa ${t.icon || 'fa-bookmark'} w3-xlarge w3-text-theme`}></i>
              <p style={{ margin: '6px 0 2px', fontWeight: 700, fontSize: 'var(--gs-text-sm)' }}>{t.label}</p>
              <p className="w3-tiny w3-text-grey" style={{ margin: 0 }}>{t.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Name + Settings */}
      {selectedPreset && (
        <div className="w3-card w3-round w3-white w3-padding w3-margin-top w3-animate-opacity">
          <Section
            label="The basics"
            action={
              selectedPreset !== 'custom' && (
                <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                  onClick={() => setShowCustomSettings(!showCustomSettings)}>
                  <i className="fa fa-cog w3-margin-right"></i>
                  {showCustomSettings ? 'Hide settings' : 'Customise settings'}
                </button>
              )
            }>
            <div className="gs-field-grid" style={{ '--gs-cols': '2fr 1fr 1fr' }}>
              <Field label="Competition name" htmlFor="new-comp-name">
                <input id="new-comp-name" className="w3-input" placeholder="e.g. 2026 Championship Singles"
                  autoFocus value={compForm.name}
                  onChange={e => setCompForm(f => ({ ...f, name: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && createComp()} />
              </Field>
              <Field label="Starts" htmlFor="new-comp-start">
                <input id="new-comp-start" className="w3-input" type="date"
                  value={compForm.startDate || ''}
                  onChange={e => { const v = e.target.value; setCompForm(f => ({ ...f, startDate: v, ...(endDateTouched.current ? {} : { endDate: v }) })); }} />
              </Field>
              <Field label="Ends" htmlFor="new-comp-end">
                <input id="new-comp-end" className="w3-input" type="date"
                  value={compForm.endDate || ''}
                  onChange={e => { endDateTouched.current = true; setCompForm(f => ({ ...f, endDate: e.target.value })); }} />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: 'var(--gs-space-2)', flexWrap: 'wrap', marginTop: 'var(--gs-space-4)' }}>
              <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                onClick={() => openCompetitionRulesPicker('new')}
                title="Choose which rules apply to this competition">
                <i className="fa fa-book w3-margin-right"></i>Competition Rules ({(compForm.appliedRules || []).length})
              </button>
              <button className="w3-button w3-round w3-theme-l4" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap' }}
                onClick={saveClubDefaults} title={isSuperAdmin && !selectedClubId ? 'Save current settings as the site-wide defaults for all clubs' : 'Save current settings as your club\'s defaults for new competitions'}>
                <i className="fa fa-save w3-margin-right"></i>{isSuperAdmin && !selectedClubId ? 'Save site defaults' : 'Save club defaults'}
              </button>
              <button className="w3-button w3-round" style={{ minHeight: 'var(--gs-control-md)', whiteSpace: 'nowrap', marginLeft: 'auto', color: 'var(--gs-text-muted)' }}
                onClick={restoreSiteDefaults} title="Reset to the original site defaults and clear saved defaults">
                <i className="fa fa-undo w3-margin-right"></i>Restore site defaults
              </button>
            </div>
          </Section>

          {showCustomSettings && (
            <CompetitionSettingsFields
              form={compForm}
              setForm={setCompForm}
              clubs={clubs}
              clubId={selectedClubId || (currentUser && currentUser.ClubId)}
              variant="create" />
          )}

          <button className="w3-button w3-theme w3-round-large w3-large w3-block" onClick={createComp} disabled={!canCreateCompetition}>
            <i className="fa fa-check w3-margin-right"></i>Create Competition
          </button>
          <button className="w3-button w3-light-grey w3-round w3-block w3-margin-top" onClick={saveAsTemplate}>
            <i className="fa fa-bookmark w3-margin-right"></i>Save as Template
          </button>
        </div>
      )}
    </div>
  );
}

export default NewCompView;
